import React, { useState, useEffect, useRef, useCallback } from "react";
import { Typography, Box, Paper, Divider } from "@mui/material";
import NewsTable from "../components/NewsDashboard/NewsTable";
import LeftMenu from "../components/Layouts/LeftMenu";
import Header from "../components/Header";
import useAuth from "../useAuth";
import 'react-toastify/dist/ReactToastify.css';
import api_client from "../components/Authentication/axiosConfig";
import { toast } from "react-toastify";
import { format } from "date-fns";

const NewsHighlightsPage = () => {
  useAuth();

  const [highlightsData, setHighlightsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  // Start with hasMore as false until we confirm there is more data
  const [hasMore, setHasMore] = useState(false);
  const observer = useRef(null);
  const pageSize = 10;

  // Function to fetch highlights data with comprehensive error handling
  const fetchHighlightsData = useCallback(async (pageNum = 1) => {
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      
      console.log(`Fetching highlights for page ${pageNum}`);
      
      const response = await api_client.get('/news_highlights/list_by_date/', {
        params: {
          page: pageNum,
          page_size: pageSize
        }
      });
      
      // Log raw response for debugging
      console.log('API Response:', {
        status: response.status,
        data: response.data,
        isArray: Array.isArray(response.data),
        type: typeof response.data
      });
      
      // Handle all possible response formats
      if (response && response.status === 200) {
        
        // Case 1: Response is an empty array (non-paginated) - []
        if (Array.isArray(response.data) && response.data.length === 0) {
          console.log("Server returned empty array, no highlights available");
          setHighlightsData([]);
          setHasMore(false);
          return;
        }
        
        // Case 2: Response is an empty object - {}
        if (typeof response.data === 'object' && 
            Object.keys(response.data).length === 0) {
          console.log("Server returned empty object, no highlights available");
          setHighlightsData([]);
          setHasMore(false);
          return;
        }
        
        // Case 3: Standard paginated response with count=0
        if (response.data && 
            response.data.hasOwnProperty('count') && 
            response.data.count === 0) {
          console.log("Server returned paginated response with count=0");
          setHighlightsData([]);
          setHasMore(false);
          return;
        }
        
        // Case 4: Paginated response with empty results array
        if (response.data && 
            response.data.hasOwnProperty('results') && 
            (!response.data.results || response.data.results.length === 0)) {
          console.log("Server returned paginated response with empty results array");
          
          // If it's the first page, reset the data
          if (pageNum === 1) {
            setHighlightsData([]);
          }
          
          setHasMore(false);
          return;
        }
        
        // Case 5: Valid paginated response with data
        if (response.data && 
            response.data.hasOwnProperty('results') && 
            Array.isArray(response.data.results) && 
            response.data.results.length > 0) {
          
          console.log(`Found ${response.data.results.length} highlights in paginated response`);
          
          // Update data based on page number
          if (pageNum === 1) {
            setHighlightsData(response.data.results);
          } else {
            setHighlightsData(prevData => [...prevData, ...response.data.results]);
          }
          
          // Determine if there are more pages to load
          const hasNextPage = response.data.next !== null;
          setHasMore(hasNextPage);
          
          console.log(`Has more pages: ${hasNextPage}`);
          return;
        }
        
        // Case 6: Unexpected response format, but still valid (status 200)
        console.warn("Received unexpected but valid response format:", response.data);
        
        // Try to extract data if possible
        if (response.data && typeof response.data === 'object') {
          const possibleData = extractHighlightsFromUnknownFormat(response.data);
          
          if (possibleData.length > 0) {
            console.log(`Extracted ${possibleData.length} highlights from unexpected format`);
            
            if (pageNum === 1) {
              setHighlightsData(possibleData);
            } else {
              setHighlightsData(prevData => [...prevData, ...possibleData]);
            }
            
            // Assume no more pages for safety
            setHasMore(false);
            return;
          }
        }
        
        // If we reach here, we couldn't make sense of the response
        console.error("Could not interpret server response", response.data);
        toast.warning("Received an unexpected response format from server");
        
        if (pageNum === 1) {
          setHighlightsData([]);
        }
        
        setHasMore(false);
      } else {
        // Non-200 status response
        console.error("Failed to load highlights data:", response);
        toast.error(`Failed to load highlights: Server returned ${response ? response.status : 'no response'}`);
        
        if (pageNum === 1) {
          setHighlightsData([]);
        }
        
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching highlights data:", error);
      toast.error(`Error fetching highlights: ${error.message || 'Unknown error'}`);
      
      if (pageNum === 1) {
        setHighlightsData([]);
      }
      
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);
  
  // Helper function to try to extract highlights from unexpected response formats
  const extractHighlightsFromUnknownFormat = (data) => {
    // If data is an array, return it directly
    if (Array.isArray(data)) {
      return data;
    }
    
    // Look for any arrays in the response that might contain our data
    for (const key in data) {
      if (Array.isArray(data[key])) {
        return data[key];
      }
      
      // Check one level deeper for arrays
      if (data[key] && typeof data[key] === 'object') {
        for (const nestedKey in data[key]) {
          if (Array.isArray(data[key][nestedKey])) {
            return data[key][nestedKey];
          }
        }
      }
    }
    
    // Couldn't find any array data
    return [];
  };

  // Initial data fetch with error recovery
  useEffect(() => {
    let isMounted = true;
    
    const initialFetch = async () => {
      try {
        await fetchHighlightsData();
      } catch (error) {
        console.error("Failed to fetch initial highlights data:", error);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setHighlightsData([]);
          setHasMore(false);
          setIsLoading(false);
        }
      }
    };
    
    initialFetch();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Reset page state when needed
  useEffect(() => {
    // Condition 1: Reset when we detect there's no more data
    if (hasMore === false && page > 1) {
      console.log("No more data available, resetting page to 1");
      setPage(1);
    }
    
    // Condition 2: Reset when we have no data at all
    if (highlightsData.length === 0 && page > 1) {
      console.log("No data available, resetting page to 1");
      setPage(1);
    }
  }, [hasMore, page, highlightsData.length]);

  // Setup intersection observer for infinite scrolling - only if we have data and more to load
  const lastHighlightElementRef = useCallback(node => {
    // First clean up any existing observer
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
    
    // Don't create an observer if we're loading, have no data, or no more data to fetch
    if (isLoading || !hasMore || highlightsData.length === 0) {
      console.log("Skipping observer setup - conditions not met", {
        isLoading,
        hasMore,
        dataLength: highlightsData.length
      });
      return;
    }
    
    // Don't create observer if node doesn't exist
    if (!node) {
      console.log("Cannot create observer - no target node provided");
      return;
    }
    
    console.log("Creating intersection observer for infinite scrolling");
    
    // Create new observer only if we have more data to load
    observer.current = new IntersectionObserver(entries => {
      // Double-check conditions before loading more data
      if (entries[0].isIntersecting && hasMore && !isLoading) {
        console.log("Last element visible, loading next page");
        setPage(prevPage => prevPage + 1);
      }
    });
    
    // Attach the observer
    observer.current.observe(node);
    console.log("Observer attached to last highlight element");
  }, [isLoading, hasMore, highlightsData.length]);
  
  // Cleanup observer when component unmounts
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    };
  }, []);
  
  // Load more data when page changes, but only if conditions are right
  useEffect(() => {
    // All these conditions must be true to load the next page:
    if (page > 1 && hasMore === true && !isLoading && highlightsData.length > 0) {
      console.log(`Loading page ${page}`);
      fetchHighlightsData(page);
    } else if (page > 1) {
      // Log debug info about why we're not loading despite page change
      console.log("Page changed but not loading more data because:", {
        page,
        hasMore,
        isLoading,
        dataLength: highlightsData.length
      });
    }
  }, [page, fetchHighlightsData, hasMore, isLoading, highlightsData.length]);

  // Format date to display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, "EEEE, MMMM d, yyyy");
  };

  return (
    <>
      <Header />
      <LeftMenu />

      <div className="flex flex-col justify-center items-center" style={{ padding: "0 20px", marginLeft: "64px" }}>
        {/* Empty state message */}
        {highlightsData.length === 0 && !isLoading && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              my: 4, 
              width: '100%', 
              textAlign: 'center',
              backgroundColor: '#f8f8f8' 
            }}
          >
            <Typography variant="body1" sx={{ my: 1 }}>
              No highlights available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Highlights will appear here when they are available
            </Typography>
          </Paper>
        )}
        
        {/* Actual highlight content */}
        {highlightsData.length > 0 && (
          <>
            {highlightsData.map((highlightDay, index) => {
              const isLast = index === highlightsData.length - 1;
              
              return (
                <Box
                  key={highlightDay.date}
                  ref={isLast ? lastHighlightElementRef : null}
                  sx={{ 
                    width: '100%', 
                    mb: 6,
                    pb: 2
                  }}
                >
                  <Paper elevation={3} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                      {formatDate(highlightDay.date)}
                    </Typography>
                  </Paper>
                  
                  <Box sx={{ overflow: 'hidden' }}>
                    {/* Use the existing NewsTable component but provide the news data directly */}
                    <CustomNewsTable newsData={highlightDay.news} />
                  </Box>
                  
                  {!isLast && <Divider sx={{ mt: 4, mb: 2 }} />}
                </Box>
              );
            })}
            
            {/* End of list message */}
            {!hasMore && !isLoading && (
              <Typography variant="body2" sx={{ my: 3, py: 2, textAlign: 'center', color: 'text.secondary' }}>
                No more highlights to load
              </Typography>
            )}
          </>
        )}
        
        {/* Loading indicator */}
        {isLoading && (
          <Paper elevation={1} sx={{ p: 3, my: 3, textAlign: 'center' }}>
            <Typography variant="body1">
              Loading highlights...
            </Typography>
          </Paper>
        )}
      </div>
    </>
  );
};

// Custom component that wraps NewsTable but provides static data instead of fetching
const CustomNewsTable = ({ newsData }) => {
  return (
    <NewsTable 
      initialNewsData={newsData} 
      disablePagination={true} 
      disableServerPagination={true}
      staticData={true}
    />
  );
};

export default NewsHighlightsPage;
